import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface TimeEntry {
  id: number
  entry_date: string | null
  user: { id: number; name: string } | null
  service_item_type: { id: number; name: string } | null
  payment_type: { id: number; name: string } | null
  started_at: string | null
  ended_at: string | null
  recorded_hours: number
  admin_minutes: number
  duration_hours: number | null
  service_hourly_cost?: number
  service_hourly_price?: number
  mileage: number
  mileage_to_job: number
  mileage_on_job: number
  mileage_from_job: number
  mileage_rate?: number
  mileage_bill_rate?: number
  total_mileage: number
  mileage_amount?: number
  vehicle_allowance: string | null
  travel_hours: number
  travel_rate?: number
  flat_cost?: number
  flat_price?: number
  billable: boolean
  notes: string | null
  time_cost?: number
  time_price?: number
  total_employee_pay?: number
  total_billable_amount?: number
  created_at: string | null
  closed_at: string | null
  approval_notes: string | null
  approved_at: string | null
  approved_by: string | null
  paid_at: string | null
  paid_reference: string | null
  mileage_paid_at: string | null
  mileage_paid_reference: string | null
  billed_at: string | null
}

interface Props {
  case_record: CaseRecord
  time_entry: TimeEntry
  can_view_financials: boolean
  can_edit: boolean
  can_approve: boolean
  can_submit: boolean
  can_mark_time_paid: boolean
  can_mark_mileage_paid: boolean
}

export default function Show({
  case_record,
  time_entry,
  can_view_financials,
  can_edit,
  can_approve,
  can_submit,
  can_mark_time_paid,
  can_mark_mileage_paid,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Time Entries", href: `/cases/${case_record.id}/time_entries` },
    {
      title: "Details",
      href: `/cases/${case_record.id}/time_entries/${time_entry.id}`,
    },
  ]

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDateTime = (date: string | null) => {
    if (!date) return "-"
    return new Date(date).toLocaleString()
  }

  const formatDate = (date: string | null) => {
    return (
      formatDateUtil(date, {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }) || "-"
    )
  }

  const isBlockTime = time_entry.admin_minutes > 0

  const handleMarkPaid = (kind: "time" | "mileage") => {
    const label = kind === "time" ? "time" : "mileage"
    const reference = window.prompt(
      `Payment reference for ${label} (optional):`,
      "",
    )
    if (reference === null) return
    router.patch(
      `/cases/${case_record.id}/time_entries/${time_entry.id}/mark_${kind}_paid`,
      { reference },
    )
  }

  const canShowMarkTimePaid =
    can_mark_time_paid && !!time_entry.approved_at && !time_entry.paid_at
  const canShowMarkMileagePaid =
    can_mark_mileage_paid &&
    !!time_entry.approved_at &&
    !time_entry.mileage_paid_at &&
    time_entry.vehicle_allowance === "Yes" &&
    time_entry.total_mileage > 0
  const hasMileageBreakdown =
    time_entry.mileage_to_job > 0 ||
    time_entry.mileage_on_job > 0 ||
    time_entry.mileage_from_job > 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Time Entry - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Time Entry Details</h1>
          <div className="space-x-2">
            {can_submit && (
              <button
                onClick={() =>
                  router.patch(
                    `/cases/${case_record.id}/time_entries/${time_entry.id}/submit`,
                  )
                }
                className="bg-hpi-yellow hover:bg-hpi-yellow/80 rounded px-4 py-2 text-white"
              >
                Submit for Approval
              </button>
            )}
            {can_edit && (
              <Link
                href={`/cases/${case_record.id}/time_entries/${time_entry.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
            )}
            {canShowMarkTimePaid && (
              <button
                onClick={() => handleMarkPaid("time")}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Mark Time Paid
              </button>
            )}
            {canShowMarkMileagePaid && (
              <button
                onClick={() => handleMarkPaid("mileage")}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Mark Mileage Paid
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Basic Info */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Basic Information</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Date</dt>
                <dd>{time_entry.entry_date}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Employee</dt>
                <dd>{time_entry.user?.name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Service Type</dt>
                <dd>{time_entry.service_item_type?.name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Payment Type</dt>
                <dd>{time_entry.payment_type?.name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Billable</dt>
                <dd>{time_entry.billable ? "Yes" : "No"}</dd>
              </div>
            </dl>
          </div>

          {/* Time */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Time</h2>
            <dl className="space-y-3">
              {isBlockTime ? (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Block Time</dt>
                  <dd>{formatMinutes(time_entry.admin_minutes)}</dd>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Start Time</dt>
                    <dd>{formatDateTime(time_entry.started_at)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">End Time</dt>
                    <dd>{formatDateTime(time_entry.ended_at)}</dd>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Recorded Hours</dt>
                <dd>{time_entry.recorded_hours.toFixed(2)}</dd>
              </div>
              {time_entry.travel_hours > 0 && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Travel</dt>
                  <dd>{time_entry.travel_hours}h</dd>
                </div>
              )}
              {can_view_financials && (
                <>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Hourly Cost</dt>
                    <dd>
                      {formatCurrency(time_entry.service_hourly_cost ?? 0)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Hourly Price</dt>
                    <dd>
                      {formatCurrency(time_entry.service_hourly_price ?? 0)}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {/* Mileage */}
          {time_entry.total_mileage > 0 && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Mileage</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Total</dt>
                  <dd className="font-semibold">
                    {time_entry.total_mileage} miles
                  </dd>
                </div>
                {hasMileageBreakdown && (
                  <>
                    <div className="flex justify-between">
                      <dt className="text-hpi-med-gray">To Job</dt>
                      <dd>{time_entry.mileage_to_job} miles</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-hpi-med-gray">On the Job</dt>
                      <dd>{time_entry.mileage_on_job} miles</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-hpi-med-gray">From Job</dt>
                      <dd>{time_entry.mileage_from_job} miles</dd>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Reimbursement</dt>
                  <dd>
                    {time_entry.vehicle_allowance === "Yes" ? "Yes" : "No"}
                  </dd>
                </div>
                {can_view_financials && (
                  <>
                    <div className="flex justify-between">
                      <dt className="text-hpi-med-gray">Bill Rate</dt>
                      <dd>
                        {formatCurrency(time_entry.mileage_bill_rate ?? 0)}/mi
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-hpi-med-gray">Mileage Amount</dt>
                      <dd>{formatCurrency(time_entry.mileage_amount ?? 0)}</dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          )}

          {/* Workflow Status */}
          <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
            <h2 className="mb-4 text-lg font-semibold">Workflow Status</h2>
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <h3 className="font-medium">Added</h3>
                <div className="text-hpi-med-gray mt-1 text-sm">
                  {formatDate(time_entry.created_at)}
                </div>
              </div>
              <div>
                <h3 className="font-medium">Submitted</h3>
                {time_entry.closed_at ? (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    {formatDate(time_entry.closed_at)}
                  </div>
                ) : (
                  <div className="text-hpi-yellow mt-1 text-sm">
                    Not Submitted
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Approval</h3>
                {time_entry.approved_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Approved</div>
                    <div className="text-hpi-med-gray">
                      by {time_entry.approved_by}
                    </div>
                    <div className="text-hpi-med-gray">
                      {formatDate(time_entry.approved_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-yellow mt-1 text-sm">
                    Pending Approval
                  </div>
                )}
              </div>
              {can_view_financials && (
                <div>
                  <h3 className="font-medium">Time Paid</h3>
                  {time_entry.paid_at ? (
                    <div className="mt-1 text-sm">
                      <div className="text-hpi-dark-green">Paid</div>
                      {time_entry.paid_reference && (
                        <div className="text-hpi-med-gray">
                          Ref: {time_entry.paid_reference}
                        </div>
                      )}
                      <div className="text-hpi-med-gray">
                        {formatDate(time_entry.paid_at)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-hpi-med-gray mt-1 text-sm">
                      Not Yet Paid
                    </div>
                  )}
                </div>
              )}
              {can_view_financials && time_entry.total_mileage > 0 && (
                <div>
                  <h3 className="font-medium">Mileage Paid</h3>
                  {time_entry.mileage_paid_at ? (
                    <div className="mt-1 text-sm">
                      <div className="text-hpi-dark-green">Paid</div>
                      {time_entry.mileage_paid_reference && (
                        <div className="text-hpi-med-gray">
                          Ref: {time_entry.mileage_paid_reference}
                        </div>
                      )}
                      <div className="text-hpi-med-gray">
                        {formatDate(time_entry.mileage_paid_at)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-hpi-med-gray mt-1 text-sm">
                      Not Yet Paid
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Approve / Reject Panel */}
          {can_approve && !time_entry.approved_at && time_entry.closed_at && (
            <ApproveRejectPanel
              approveUrl={`/cases/${case_record.id}/time_entries/${time_entry.id}/approve`}
              rejectUrl={`/cases/${case_record.id}/time_entries/${time_entry.id}/reject`}
            />
          )}

          {/* Approval History */}
          {time_entry.approval_notes && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Approval History</h2>
              <pre className="text-hpi-dark-gray text-sm whitespace-pre-wrap">
                {time_entry.approval_notes}
              </pre>
            </div>
          )}

          {/* Notes */}
          {time_entry.notes && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Notes</h2>
              <p className="text-hpi-dark-gray whitespace-pre-wrap">
                {time_entry.notes}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            href={`/cases/${case_record.id}/time_entries`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Time Entries
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}

function ApproveRejectPanel({
  approveUrl,
  rejectUrl,
}: {
  approveUrl: string
  rejectUrl: string
}) {
  const [notes, setNotes] = useState("")
  const [processing, setProcessing] = useState(false)

  const handleApprove = () => {
    setProcessing(true)
    router.patch(
      approveUrl,
      { notes },
      { onFinish: () => setProcessing(false) },
    )
  }

  const handleReject = () => {
    if (!notes.trim()) {
      alert("Please provide a reason for rejection.")
      return
    }
    setProcessing(true)
    router.patch(rejectUrl, { notes }, { onFinish: () => setProcessing(false) })
  }

  return (
    <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-6 shadow md:col-span-2">
      <h2 className="mb-4 text-lg font-semibold">Review</h2>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional note (required for rejection)"
        rows={3}
        className="border-hpi-light-gray mb-4 w-full rounded border p-2 text-sm"
      />
      <div className="flex gap-3">
        <button
          onClick={handleApprove}
          disabled={processing}
          className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-6 py-2 text-white disabled:opacity-50"
        >
          Approve
        </button>
        <button
          onClick={handleReject}
          disabled={processing}
          className="rounded bg-red-600 px-6 py-2 text-white hover:bg-red-700 disabled:opacity-50"
        >
          Reject
        </button>
      </div>
    </div>
  )
}

function formatMinutes(totalMin: number): string {
  const hours = Math.floor(totalMin / 60)
  const mins = totalMin % 60
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`
  if (hours > 0) return `${hours}h`
  return `${mins}m`
}
